/**
 * ============================================================
 * KEJJA OS — src/hooks/useSettings.ts + useSystem.ts (combined)
 * ============================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  settingsApi, systemApi,
  type ProfileUpdatePayload, type PrefsUpdatePayload,
  type HomeDetailsPayload, type CreateInvitePayload,
} from '@/lib/settingsApi';

const invalidateSettings = (qc: ReturnType<typeof useQueryClient>, homeId?: string) => {
  qc.invalidateQueries({ queryKey: ['settings'] });
  if (homeId) qc.invalidateQueries({ queryKey: ['settings', homeId] });
};

// ============================================================
// Profile
// ============================================================

export function useProfile() {
  return useQuery({
    queryKey: ['settings', 'profile'],
    queryFn: () => settingsApi.getProfile(),
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ProfileUpdatePayload) => settingsApi.updateProfile(body),
    onSuccess: () => invalidateSettings(qc),
  });
}

export function useAcceptInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (inviteCode: string) => settingsApi.acceptInvite(inviteCode),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['homes'] });
      qc.invalidateQueries({ queryKey: ['settings'] });
    },
  });
}

// ============================================================
// Home preferences
// ============================================================

export function useHomePreferences(homeId: string | undefined) {
  return useQuery({
    queryKey: ['settings', homeId, 'preferences'],
    queryFn: () => settingsApi.getPreferences(homeId!),
    enabled: Boolean(homeId),
  });
}

export function useUpdateHomePreferences(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PrefsUpdatePayload) => settingsApi.updatePreferences(homeId!, body),
    onSuccess: () => invalidateSettings(qc, homeId),
  });
}

export function useUpdateHomeDetails(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: HomeDetailsPayload) => settingsApi.updateHomeDetails(homeId!, body),
    onSuccess: () => {
      invalidateSettings(qc, homeId);
      qc.invalidateQueries({ queryKey: ['homes'] });
    },
  });
}

// ============================================================
// Members + invites
// ============================================================

export function useMembers(homeId: string | undefined) {
  return useQuery({
    queryKey: ['settings', homeId, 'members'],
    queryFn: () => settingsApi.listMembers(homeId!),
    enabled: Boolean(homeId),
  });
}

export function useChangeRole(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      settingsApi.changeRole(homeId!, userId, role),
    onSuccess: () => invalidateSettings(qc, homeId),
  });
}

export function useRemoveMember(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => settingsApi.removeMember(homeId!, userId),
    onSuccess: () => invalidateSettings(qc, homeId),
  });
}

export function useLeaveHome(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => settingsApi.leaveHome(homeId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['homes'] });
      invalidateSettings(qc, homeId);
    },
  });
}

export function useInvites(homeId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['settings', homeId, 'invites'],
    queryFn: () => settingsApi.listInvites(homeId!),
    enabled: Boolean(homeId) && enabled,
  });
}

export function useCreateInvite(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateInvitePayload) => settingsApi.createInvite(homeId!, body),
    onSuccess: () => invalidateSettings(qc, homeId),
  });
}

export function useRevokeInvite(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (inviteId: string) => settingsApi.revokeInvite(homeId!, inviteId),
    onSuccess: () => invalidateSettings(qc, homeId),
  });
}

// ============================================================
// System
// ============================================================

export function useVersion() {
  return useQuery({
    queryKey: ['system', 'version'],
    queryFn: () => systemApi.getVersion(),
    staleTime: 60 * 1000,
  });
}

export function useActivity(homeId: string | undefined, limit = 50) {
  return useQuery({
    queryKey: ['system', homeId, 'activity', limit],
    queryFn: () => systemApi.listActivity(homeId!, limit),
    enabled: Boolean(homeId),
  });
}

export function useIntegrations(homeId: string | undefined) {
  return useQuery({
    queryKey: ['system', homeId, 'integrations'],
    queryFn: () => systemApi.getIntegrations(homeId!),
    enabled: Boolean(homeId),
  });
}

export function useDeleteHome(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => systemApi.deleteHome(homeId!),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['homes'] }); },
  });
}

export function useDeleteAccount() {
  return useMutation({
    mutationFn: () => systemApi.deleteAccount(),
  });
}
